import create from 'vue-zustand';

import { OnboardingAdminRequests as requests } from '@/services/requests';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { IApiResponse } from '@/types/interfaces';
import { CorporateDocumentType, DocumentSide } from '@/types/interfaces/Document.interface';
import { formatOnboardingPersonalDocuments } from '@/utils';
import { getRequestHandler, requestHandler } from '@/utils/requestHandler';

/*
* Zustand store
  can be reusable too with utils/create-store const createStore = create
  react (import create from 'zustand';) vue import create from 'vue-zustand';
  export const usePersonalOnboardingStore = createStore<State>(PersonalOnboardingStore);
* */

const initialState: Onboarding.SoleTrader.FullApplication & Onboarding.SoleTrader.ApplicationStore = {
  id: '',
  email: '',
  phone: '',
  completedStages: [],
  applicationStatus: 'ONBOARDING',
  details: {
    firstName: '',
    lastName: '',
    nationalityIso3: '',
    dateOfBirth: '',
    occupation: '',
    countryOfResidenceIso3: '',
    mailingAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      countryIso3: '',
      postalCode: '',
    },
  },
  userDetails: {
    userId: '',
    organizationId: '',
    profileOrganizationId: '',
    permissions: [],
    email: '',
    phone: '',
  },
  riskLevel: undefined,
  createAt: '',
  updatedAt: '',
  blocked: false,
  isApplicationLoaded: false,
  contract: undefined,
  documents: undefined,
};

export const useOnboardingAdminSoleTraderStore = create(() => initialState);

const { getState, setState } = useOnboardingAdminSoleTraderStore;

export function resetStore() {
  setState(initialState);
}

function updateApplication(data: Onboarding.SoleTrader.FullApplication) {
  setState({
    ...data,
    documents: formatOnboardingPersonalDocuments(data.documents),
  });
}

type SoleTraderDocument = {
  type: CorporateDocumentType;
  formData: FormData;
  issuingCountryIso3?: string;
  side?: DocumentSide;
};

export const onboardingAdminSoleTraderStoreActions = {
  async fetchApplication(
    id: string,
    force?: boolean,
  ): Promise<IApiResponse<Onboarding.SoleTrader.FullApplication> | null> {
    if (!force && getState().isApplicationLoaded) return null;
    return requestHandler(() => requests.SoleTrader.getApplication(id), {
      pending: (newValue) => setState({ isApplicationLoaded: !newValue }),
      onSuccess: updateApplication,
    });
  },
  updateDetails: getRequestHandler(
    (data: Onboarding.SoleTrader.Details) => requests.SoleTrader.updateDetails(getState().id, data),
    { onSuccess: updateApplication },
  ),
  verifyApplication: getRequestHandler(
    () => requests.SoleTrader.verifyApplication(getState().id),
    { onSuccess: updateApplication },
  ),
  updateStatus: getRequestHandler(
    (status: Exclude<Onboarding.UpdateApplicationStatus, 'verified'>) => {
      const { id } = getState();
      switch (status) {
        case 'decline':
          return requests.SoleTrader.declineApplication(id);
        case 'no-response':
          return requests.SoleTrader.noResponseApplication(id);
        case 'onboarding':
          return requests.SoleTrader.onOnboardingApplication(id);
        case 'non-verified':
          return requests.SoleTrader.onNonVerifiedApplication(id);
        default:
          return requests.SoleTrader.updateStatus(id, status);
      }
    },
    { onSuccess: updateApplication },
  ),
  documents: {
    get: getRequestHandler(
      (fileData: {
        type: CorporateDocumentType;
        side: DocumentSide;
      }) => requests.SoleTrader.document.get(getState().id, fileData),
    ),
    upload: getRequestHandler(
      (fileData: SoleTraderDocument) => requests.SoleTrader.document.upload(getState().id, fileData),
      { onSuccess: updateApplication },
    ),
  },
  updateContract: (id: string, contractId: string) => requestHandler(
    () => requests.SoleTrader.updateContract(id, contractId),
    {
      errorNotification: false,
      onSuccess: updateApplication,
    },
  ),
  uploadDocument: getRequestHandler(
    (fileData: SoleTraderDocument) => requests.SoleTrader.document.upload(getState().id, fileData),
    { onSuccess: updateApplication },
  ),
  updateRiskLevel: getRequestHandler(
    (riskLevel: Onboarding.RiskLevel) => requests.SoleTrader.updateRiskLevel(getState().id, riskLevel),
    { onSuccess: updateApplication },
  ),
  async toggleBlockStatus() {
    const { id, blocked } = getState();

    const request = blocked
      ? requests.Shared.unblockApplication
      : requests.Shared.blockApplication;

    return getRequestHandler(
      () => request(id),
      { onSuccess: (res) => setState({ blocked: res.blocked }) },
    );
  },
  updateUserContacts: getRequestHandler(
    (newContact: Record<string, string | undefined>) => requests.Shared.updateUserContact(getState().id, newContact),
  ),
};
